import {
  Checkbox,
  DatePicker,
  FormInput,
  FormSelect,
  Hint,
  Modal,
  ModalSizeEnum,
  NeutralButton,
  PrimaryButton,
  RadioGroup,
  TimePicker,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import 'modules/courses/courses/api/courses-api'
import { editReminderSettings, enableReminder } from 'modules/courses/courses/api/courses-api'
import { CourseEditApiInterface } from 'modules/courses/courses/types/course-interface'
import {
  ReminderEditInterface,
  ReminderErrorResponseInterface,
  ReminderErrorsInterface,
  ReminderInterface,
  RepeatUnitEnum,
  SendingFrequencyEnum,
} from 'modules/courses/courses/types/reminder-interface'
import {
  getConditionsData,
  getSendingFrequencyOptions,
} from 'modules/courses/courses/utils/reminder-utils'
import React, { FC, useEffect, useState } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { FieldLabel } from 'shared/components/form/field-label'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { getToday } from 'shared/utils/get-dates'
import { KeyedMutator } from 'swr'
import { twMerge } from 'tailwind-merge'
import { SaveIcon } from '@systemeio/ui/icons'

interface Props {
  courseId: number
  data?: ReminderInterface
  opened: boolean
  onClose: () => void
  isDataLoading: boolean
  mutateData: KeyedMutator<ReminderInterface>
  mutateCourse: KeyedMutator<CourseEditApiInterface>
  hasReminderEmailSettings: boolean
}

const defaultErrors: ReminderErrorsInterface = {
  condition: '',
  conditionDays: '',
  reminderTime: '',
  sendingFrequency: '',
  minProgress: '',
  started: '',
  resendSettings: {
    repeatEvery: '',
    finishAfter: '',
    maxOccurrences: '',
  },
}

const initialValues: ReminderEditInterface = {
  condition: undefined,
  conditionDays: 0,
  reminderTime: null,
  notStarted: false,
  started: false,
  minProgress: 0,
  sendingFrequency: undefined,
  resendSettings: undefined,
}

const ReminderSettingsModal: FC<Props> = ({
  courseId,
  data,
  opened,
  onClose,
  isDataLoading,
  mutateData,
  mutateCourse,
  hasReminderEmailSettings,
}) => {
  const { user, intlLocale } = useUser()
  const { t } = useLocoTranslation()

  const [tempState, setTempState] = useState<ReminderEditInterface>(initialValues)

  useEffect(() => {
    if (data) {
      setTempState({
        condition: data?.condition,
        conditionDays: data?.conditionDays,
        reminderTime: data?.reminderTime,
        notStarted: data?.notStarted,
        started: data?.started,
        minProgress: data?.minProgress,
        sendingFrequency: data?.sendingFrequency,
        resendSettings: data?.resendSettings,
      })
    } else {
      setTempState(initialValues)
    }
  }, [data, opened])

  const [errors, setErrors] = useState<ReminderErrorsInterface>(defaultErrors)
  const [isFetching, setIsFetching] = useState(false)

  const onSave = async () => {
    try {
      setErrors(defaultErrors)

      setIsFetching(true)

      const { resendSettings, ...tempStateWithoutResendSettings } = tempState

      const { data } = await editReminderSettings(
        courseId,
        tempState.sendingFrequency === SendingFrequencyEnum.Once
          ? tempStateWithoutResendSettings
          : tempState,
      )

      await mutateCourse(currentData => {
        if (currentData) {
          return {
            ...currentData,
            hasReminderSettings: true,
          }
        }
      }, false)

      await mutateData(data, false)

      if (hasReminderEmailSettings) {
        await enableReminder(courseId)
      }

      toast.success(t('global.changes_saved'))

      onClose()
    } catch (e) {
      if (e instanceof BadRequest) {
        const errors = e.errors as unknown as ReminderErrorResponseInterface

        setErrors({
          condition: errors.fields.condition?.join(''),
          conditionDays: errors.fields.conditionDays?.join(''),
          reminderTime: errors.fields.reminderTime?.join(''),
          sendingFrequency: errors.fields.sendingFrequency?.join(''),
          minProgress: errors.fields.minProgress?.join(''),
          started: errors.fields.started?.join(''),
          resendSettings: {
            repeatEvery: errors.fields.resendSettings?.repeatEvery?.join(''),
            finishAfter: errors.fields.resendSettings?.finishAfter?.join(''),
            maxOccurrences: errors.fields.resendSettings?.maxOccurrences?.join(''),
          },
        })
      } else {
        toast.error(t('global.error'))
      }
    } finally {
      setIsFetching(false)
    }
  }

  const onChangeSendingFrequency = (value: SendingFrequencyEnum) => {
    switch (value) {
      case SendingFrequencyEnum.Once:
        setTempState(prevState => ({
          ...prevState,
          sendingFrequency: value,
          resendSettings: undefined,
        }))
        break
      case SendingFrequencyEnum.Repeat:
        setTempState(prevState => ({
          ...prevState,
          sendingFrequency: SendingFrequencyEnum.Repeat,
          resendSettings: !!prevState.resendSettings
            ? prevState.resendSettings
            : {
                repeatEvery: 0,
                repeatUnit: RepeatUnitEnum.Day,
                finishAfter: null,
                maxOccurrences: null,
              },
        }))
    }
  }

  const isEndDateChecked = tempState.resendSettings?.finishAfter !== null

  const onChangeEndDateCheckbox = (checked: boolean) =>
    setTempState(prevState => ({
      ...prevState,
      resendSettings: prevState.resendSettings
        ? {
            ...prevState.resendSettings,
            finishAfter: checked ? '' : null,
          }
        : prevState.resendSettings,
    }))

  const isMaxOccurrencesChecked = tempState.resendSettings?.maxOccurrences !== null

  const onChangeMaxOccurrencesCheckbox = (checked: boolean) =>
    setTempState(prevState => ({
      ...prevState,
      resendSettings: prevState.resendSettings
        ? {
            ...prevState.resendSettings,
            maxOccurrences: checked ? 0 : null,
          }
        : prevState.resendSettings,
    }))

  const isModalLoading = isDataLoading || isFetching

  return (
    <Modal
      isFetching={isModalLoading}
      size={ModalSizeEnum.medium}
      title={t('dashboard.course.reminder.settings.title')}
      opened={opened}
      onClose={onClose}
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-8 overflow-y-auto">
          <div className="grid grid-cols-2 gap-7 gap-x-5 lg:gap-y-10">
            <div className="col-span-2 md:col-span-1">
              <FormSelect
                value={tempState.condition}
                data={getConditionsData(t)}
                onChange={value => {
                  setTempState(prevState => ({ ...prevState, condition: value }))
                }}
                label={t('dashboard.course.reminder.settings.condition')}
                placeholder={t('dashboard.course.reminder.settings.select_condition')}
                error={errors.condition}
                withoutCloseIcon
              />
            </div>
            <div className="col-span-2 flex flex-col gap-1 md:col-span-1">
              <FormInput
                className={'w-full md:w-[160px]'}
                errorAndDescriptionClassName={errors.conditionDays && 'hidden'}
                type={'number'}
                addonAfter={
                  tempState.conditionDays > 1
                    ? t('dashboard.course.reminder.settings.condition_days')
                    : t('dashboard.course.reminder.settings.condition_day')
                }
                label={t('dashboard.course.reminder.settings.condition_days')}
                value={tempState.conditionDays || ''}
                onChange={e =>
                  setTempState(prevState => ({
                    ...prevState,
                    conditionDays: Number(e.target.value),
                  }))
                }
                error={errors.conditionDays}
              />
              {errors.conditionDays && <FieldErrorAndDescription error={errors.conditionDays} />}
            </div>
            <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
              <TimePicker
                containerClassName={'w-[200px]'}
                wrapperClassName={'w-full'}
                label={t('dashboard.course.reminder.settings.reminder_time')}
                seconds={tempState.reminderTime}
                onChangeSeconds={value =>
                  setTempState(prevState => ({ ...prevState, reminderTime: value }))
                }
                error={errors.reminderTime}
                errorClassName={'hidden'}
                userDateFormat={user?.dateFormat}
                userTimeFormat={user?.timeFormat}
              />
              {errors.reminderTime && <FieldErrorAndDescription error={errors.reminderTime} />}
            </div>
            <div className="col-span-2">
              <FieldLabel
                labelClassName={'text-sm font-medium'}
                label={t('dashboard.course.reminder.settings.whom_to_notify')}
              />
              <div className={twMerge('flex flex-col gap-[5px]', errors.started && 'text-danger')}>
                <Checkbox
                  className={'my-[5.5px]'}
                  labelClassName={errors.started && 'text-danger'}
                  value={tempState.notStarted}
                  onChange={value =>
                    setTempState(prevState => ({
                      ...prevState,
                      notStarted: value,
                    }))
                  }
                  label={t('dashboard.course.reminder.settings.not_started')}
                />
                <div>
                  <div className={'my-[5.5px] flex items-center gap-4'}>
                    <Checkbox
                      labelClassName={errors.started && 'text-danger'}
                      value={tempState.started}
                      onChange={value =>
                        setTempState(prevState => ({
                          ...prevState,
                          started: value,
                        }))
                      }
                      label={t('dashboard.course.reminder.settings.in_progress')}
                    />
                    {tempState.started && (
                      <>
                        <FormInput
                          errorAndDescriptionClassName={errors.minProgress && 'hidden'}
                          className={'w-[140px]'}
                          type={'number'}
                          value={tempState.minProgress || ''}
                          onChange={e =>
                            setTempState(prevState => ({
                              ...prevState,
                              minProgress: Number(e.target.value),
                            }))
                          }
                          error={errors.minProgress}
                          addonAfter={'%'}
                        />
                        <Hint label={t('dashboard.course.reminder.settings.min_progress_hint')} />
                      </>
                    )}
                  </div>
                  {errors.minProgress && <FieldErrorAndDescription error={errors.minProgress} />}
                </div>
              </div>
              {errors.started && (
                <FieldErrorAndDescription className={'mt-1'} error={errors.started} />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={'col-span-2'}>
              <FieldLabel
                labelClassName={'text-sm font-medium'}
                label={t('dashboard.course.reminder.settings.sending_frequency')}
              />
              <RadioGroup
                optionsWrapperClassName="block gap-1"
                optionClassName="my-[8px]"
                options={getSendingFrequencyOptions(t)}
                selected={tempState.sendingFrequency}
                onChange={onChangeSendingFrequency}
                className={'flex flex-col gap-[5px]'}
                error={errors.sendingFrequency}
              />
            </div>
            {tempState.sendingFrequency === SendingFrequencyEnum.Repeat && (
              <>
                <div className="col-span-2 md:col-span-1">
                  <FieldLabel
                    labelClassName={'text-sm font-medium'}
                    label={t('dashboard.course.reminder.settings.repeat_every')}
                  />
                  <FormInput
                    type={'number'}
                    value={tempState.resendSettings?.repeatEvery || ''}
                    onChange={e =>
                      setTempState(prevState => ({
                        ...prevState,
                        resendSettings: {
                          ...prevState.resendSettings!,
                          repeatEvery: Number(e.target.value),
                        },
                      }))
                    }
                    error={errors.resendSettings?.repeatEvery}
                    addonAfter={
                      (
                        tempState.resendSettings?.repeatEvery
                          ? tempState.resendSettings?.repeatEvery > 1
                          : 0 > 1
                      )
                        ? t('dashboard.course.reminder.settings.repeat_every_days')
                        : t('dashboard.course.reminder.settings.repeat_every_day')
                    }
                  />
                </div>
                <div className="hidden lg:col-span-1 lg:block" />
                <div className="col-span-2 flex flex-col lg:col-span-1">
                  <FieldLabel
                    labelClassName={'text-sm font-medium'}
                    label={t('dashboard.course.reminder.settings.ends')}
                  />
                  <div className="flex justify-between gap-5">
                    <div className="align-center flex min-w-[61px] max-w-[61px] justify-start">
                      <Checkbox
                        value={isEndDateChecked}
                        onChange={onChangeEndDateCheckbox}
                        label={t('dashboard.course.reminder.settings.on')}
                      />
                    </div>
                    <div className="w-full">
                      <DatePicker
                        disabled={!isEndDateChecked}
                        minDate={getToday(user?.timezone)}
                        value={tempState.resendSettings?.finishAfter || null}
                        onChange={value =>
                          setTempState(prevState => ({
                            ...prevState,
                            resendSettings: prevState.resendSettings
                              ? {
                                  ...prevState.resendSettings,
                                  finishAfter: value,
                                }
                              : prevState.resendSettings,
                          }))
                        }
                        error={errors.resendSettings?.finishAfter}
                        timezone={user?.timezone}
                        createdAt={user?.createdAt}
                        intlLocale={intlLocale}
                      />
                    </div>
                  </div>
                </div>
                <div className="hidden lg:col-span-1 lg:block" />
                <div className="col-span-2 flex gap-5 lg:col-span-1 lg:justify-between">
                  <div className="align-center flex min-w-[61px] max-w-[61px] justify-start">
                    <Checkbox
                      value={isMaxOccurrencesChecked}
                      onChange={onChangeMaxOccurrencesCheckbox}
                      label={t('dashboard.course.reminder.settings.after')}
                    />
                  </div>
                  <div className="w-full max-w-[100%] lg:max-w-[calc(100%-81px)]">
                    <FormInput
                      className="w-full"
                      disabled={!isMaxOccurrencesChecked}
                      type={'number'}
                      value={tempState.resendSettings?.maxOccurrences || ''}
                      onChange={e =>
                        setTempState(prevState => ({
                          ...prevState,
                          resendSettings: prevState.resendSettings
                            ? {
                                ...prevState.resendSettings,
                                maxOccurrences: Number(e.target.value),
                              }
                            : prevState.resendSettings,
                        }))
                      }
                      addonAfter={t('dashboard.course.reminder.settings.occurrences')}
                      error={errors.resendSettings?.maxOccurrences}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <NeutralButton disabled={isModalLoading} onClick={onClose}>
            {t('global.cancel')}
          </NeutralButton>
          <PrimaryButton isFetching={isModalLoading} onClick={onSave}>
            <SaveIcon />
            {t('global.save')}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  )
}

export default ReminderSettingsModal
